<template>
  <div class="post-whole-wrapper">
    <ul>
      <li v-for="post in posts" :key="post.post_id" class="post-one-wrapper scroll-animation" v-scroll-animation>
        <router-link :to="{ name: 'HistoryDetail', params: { post_id: post.post_id } }" class="router-link">
          <div class="post-main-wrapper">
            <div class="post-image">
              <img :src="post.thumbnail_image || defaultImagePath" @error="onImageError($event, defaultImagePath)" />
            </div>
            <div class="post-texts-wrapper">
              <h2 class="post-title">{{ post.title }}</h2>
              <p class="post-text">
                <span>{{ post.text }}</span>
              </p>
            </div>
          </div>
        </router-link>
        <div class="post-sub-wrapper">
          <p class="post-date">{{ post.date_created }}</p>
          <p class="post-user">{{ post.create_user_name }}</p>
          <div class="post-likes-wrapper">
            <button @click="likePost(post.post_id)" :disabled="post.liked" class="post-likes-button">
              <div :class="['post-likes-icon', { 'svg-filter-liked': post.liked, animating: post.animating }]"></div>
            </button>
            <span>{{ post.likes ?? 0 }}</span>
          </div>
        </div>
      </li>
    </ul>
    <!-- flagPagenationがTrueだったら表示する -->
    <div v-if="flagPagenation && state.nextToken" class="view-more-wrapper">
      <button @click="fetchMorePosts">view more</button>
    </div>
  </div>
</template>

<script>
import { reactive, ref, onMounted, onUnmounted ,onBeforeUnmount} from "vue";
import axios from "axios";
import gql from "graphql-tag";

export default {
  props: {
    limit: {
      type: Number,
      default: 3, // 一度に取得する投稿数のデフォルト値
    },
    flagPagenation: {
      type: Boolean,
      default: false, // ページネーションを有効にするかどうか
    },
  },
  setup(props) {
    // デフォルト画像パスの設定
    const defaultImagePath = require('@/assets/img/common/SABA.png');

    // AppSyncやWebSocketの設定
    const apiUrlAppSync = process.env.VUE_APP_APPSYNC_ENDPOINT;
    const authToken = process.env.VUE_APP_APPSYNC_AUTH_TOKEN;
    const appsyncRealtimeEndpoint = process.env.VUE_APP_REALTTIME_ENDPOINT;
    const appsyncHost = process.env.VUE_APP_HOST_APPSYNC;
    const appsyncSubscID = "postList";

    // 投稿データと状態を保持するリアクティブな変数
    const posts = ref([]);
    const state = reactive({
      posts: [],
      nextToken: null, // 次のデータ取得用トークン
    });

    // GraphQLのクエリとミューテーション
    const incrementPostLikesMutation = gql`
      mutation incrementPostLikes($post: String!, $post_id: Int!) {
        incrementPostLikes(post: $post, post_id: $post_id) {
          post
          post_id
          likes
        }
      }
    `;

    const getAllPostsQuery = gql`
      query GetAllPosts($limit: Int, $nextToken: String) {
        getAllPosts(limit: $limit, nextToken: $nextToken) {
          items {
            post_id
            title
            text
            thumbnail_image
            date_created
            create_user_name
            likes
          }
          nextToken
        }
      }
    `;

    const onPostLikedSubscription = gql`
      subscription onPostLikedCount {
        onPostLikedCount {
          post
          post_id
          likes
        }
      }
    `;

    // 投稿データを取得する関数
    const fetchPosts = async (nextToken = null) => {
      try {
        const variables = {
          limit: props.limit,
          ...(props.flagPagenation && nextToken ? { nextToken } : {}),
        };
        const response = await axios.post(
          apiUrlAppSync,
          {
            query: getAllPostsQuery.loc.source.body,
            variables,
          },
          {
            headers: {
              Authorization: authToken,
            },
          }
        );

        const data = response.data.data.getAllPosts;
        if (data && data.items) {
          // 取得した投稿データをリアクティブな配列に追加
          posts.value.splice(posts.value.length, 0, ...data.items.map(post => ({
            ...post,
            liked: false, // 初期状態では「いいね」されていない
            animating: false, // 初期状態ではアニメーション中ではない
          })));
          state.nextToken = data.nextToken || null; // 次のデータ取得トークンを更新
        }
      } catch (error) {
        console.error("Error fetching posts:", error);
      }
    };

    // ページネーションで次の投稿データを取得する関数
    const fetchMorePosts = () => {
      if (state.nextToken) {
        fetchPosts(state.nextToken);
      } else {
        console.log("次の投稿データがありません");
      }
    };

    // 「いいね」をサーバーに送信し、投稿データを更新する関数
    const likePost = async (postId) => {
      try {
        const postIndex = posts.value.findIndex(post => post.post_id === postId);
        if (postIndex === -1) return;

        // 「いいね」状態を更新
        const updatedPost = { ...posts.value[postIndex], liked: true, animating: true };
        posts.value.splice(postIndex, 1, updatedPost);

        // サーバーにいいねを送信
        const response = await axios.post(
          apiUrlAppSync,
          {
            query: incrementPostLikesMutation.loc.source.body,
            variables: { post: "post", post_id: postId },
          },
          {
            headers: { Authorization: authToken },
          }
        );

        const data = response.data.data.incrementPostLikes;
        if (data) {
          // サーバーからのレスポンスで投稿を更新
          const updatedPostWithLikes = { ...updatedPost, likes: data.likes };
          posts.value.splice(postIndex, 1, updatedPostWithLikes);
        }

        // アニメーション終了後に状態をリセット
        setTimeout(() => {
          const postAfterAnimation = { ...posts.value[postIndex], animating: false };
          posts.value.splice(postIndex, 1, postAfterAnimation);
        }, 300);
      } catch (error) {
        console.error("Error liking post:", error);
      }
    };

    // WebSocketを使用してサブスクリプションを管理
    const subscribeToLikes = () => {
      const header = { host: appsyncHost, Authorization: authToken };
      const url =
        `${appsyncRealtimeEndpoint}?header=` +
        btoa(JSON.stringify(header)) +
        "&payload=" +
        btoa(JSON.stringify({}));

      const socket = new WebSocket(url, ["graphql-ws"]);

      socket.addEventListener("open", () => {
        console.log("WebSocketが接続されました");
        socket.send(JSON.stringify({ type: "connection_init" }));
      });

      socket.addEventListener("message", (event) => {
        try {
          const eventDataJson = JSON.parse(event.data);

          switch (eventDataJson.type) {
            case "connection_ack":
              console.log("接続が承認されました。サブスクリプションリクエストを送信します...");
              socket.send(getSubscRegistMessage(appsyncSubscID));
              break;

            case "ka":
              console.log("Keep aliveシグナルを受信しました");
              break;

            case "start_ack":
              console.log("サブスクリプションが開始されました");
              break;

            case "data":
              console.log("サブスクリプションデータを受信しました");
              const updatedPost = eventDataJson.payload.data.onPostLikedCount;
              const postIndex = posts.value.findIndex(p => p.post_id === updatedPost.post_id);
              if (postIndex !== -1) {
                const postWithUpdatedLikes = {
                  ...posts.value[postIndex],
                  likes: updatedPost.likes,
                };
                posts.value.splice(postIndex, 1, postWithUpdatedLikes);
              }
              break;

            case "complete":
              console.log("サブスクリプションを登録解除しました");
              break;

            case "error":
              console.error("サブスクリプションエラー:", eventDataJson);
              break;

            default:
              console.warn("未処理のメッセージタイプ:", eventDataJson.type);
          }
        } catch (error) {
          console.error("WebSocket メッセージ処理中にエラーが発生しました:", error);
        }
      });

      // WebSocketのクリーンアップ
      onUnmounted(() => {
        socket.close();
        console.log("WebSocket接続を終了しました");
      });

      // WebSocketを閉じる処理
      const closeSocket = () => {
        socket.send(JSON.stringify({ type: "stop", id: appsyncSubscID })); // サブスクリプション停止
        socket.close();
        console.log("WebSocket connection closed.");
      };

      // コンポーネントが破棄される前にWebSocketを閉じる
      onBeforeUnmount(() => {
        closeSocket();
      });

      // サブスクリプション登録メッセージを作成
      function getSubscRegistMessage(appsyncSubscID) {
        return JSON.stringify({
          id: appsyncSubscID,
          payload: {
            data: JSON.stringify({ query: onPostLikedSubscription.loc.source.body, variables: {} }),
            extensions: {
              authorization: {
                Authorization: authToken,
                host: appsyncHost,
              },
            },
          },
          type: "start",
        });
      }
    };

    // 画像ロードエラー時にデフォルト画像を設定
    const onImageError = (event, defaultPath) => {
      event.target.src = defaultPath;
    };

    // コンポーネントのマウント時に投稿取得＆サブスクリプション開始
    onMounted(() => {
      fetchPosts();
      subscribeToLikes();
    });

    return {
      state,
      posts,
      fetchPosts,
      fetchMorePosts,
      likePost,
      defaultImagePath,
      onImageError,
    };
  },
};
</script>

<style scoped>
.post-whole-wrapper {
  position: relative;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  width: 100%;
  height: auto;
}

.post-whole-wrapper ul {
  list-style: none;
  padding-left: 0;
}

.post-one-wrapper {
  position: relative;
  padding: 20px;
  display: flex;
  flex-direction: column;
  width: 90%;
}

.post-one-wrapper:nth-of-type(2n) {
  transform: translateX(7%);
}

.router-link {
  text-decoration: none;
  color: #493c33;
  transition: color 0.3s ease;
}

.router-link:hover {
  text-decoration: none;
  color: #69b1a9;
}

.router-link:hover img {
  transform: scale(1.01);
}

.post-main-wrapper {
  display: flex;
  flex-direction: row;
  justify-content: flex-start;
  align-items: flex-start;
  font-family: "ShipporiMincho";
  height: 200px;
  width: 100%;
  max-width: 80vw;
  overflow: hidden;
}

.post-image {
  width: 200px;
  height: 200px;
  display: flex;
  justify-content: center;
  align-items: center;
}

.post-image img {
  width: 100%;
  height: auto;
  object-fit: cover;
  transition: transform 0.3s ease;
}

.post-texts-wrapper {
  position: relative;
  padding-left: 75px;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: flex-start;
  flex: 1;
  width: 100%;
}

.post-title {
  font-size: 15px;
  font-weight: normal;
  margin: 0;
  margin-bottom: 10px;
}

.post-text {
  overflow: hidden;
  /* はみ出した部分を隠す */
  text-overflow: ellipsis;
  /* テキストがはみ出した場合に省略記号を表示 */
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 4;
  /* 表示する行数を指定 */
  white-space: normal;
  /* テキストを折り返す */
  width: 100%;
}

.post-text span {
  font-size: 13px;
  line-height: 1.8;
  margin-bottom: 10px;
  width: 100%;
}


/* 作成日と作成者情報といいね */
.post-sub-wrapper {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  transform: translateY(-35px);
  font-size: 12px;
  color: #86827e;
  flex: 1;
}

.post-date,
.post-user {
  display: inline-block;
  margin-right: 17px;
}

.post-likes-wrapper {
  display: flex;
  align-items: center;
  text-align: right;
  font-size: 14px;
  z-index: 99;
  padding: 30px;
  padding-top: 20px;
  -webkit-tap-highlight-color: transparent;
  /* スマホでのタップ時のハイライトを無効化 */
}

.post-likes-wrapper button {
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
}

.post-likes-wrapper span {
  font-size: 12px;
  color: #696867;
}

.post-likes-icon {
  width: 50px;
  height: 50px;
  cursor: pointer;
  margin-right: 0px;
  background: transparent url(@/assets/img/history/likes.svg) no-repeat top center /cover;
  filter: brightness(0) saturate(100%) invert(52%) sepia(10%) saturate(167%) hue-rotate(349deg) brightness(97%) contrast(84%);
}

.post-likes-icon:hover {
  filter: brightness(0) saturate(100%) invert(15%) sepia(87%) saturate(4756%) hue-rotate(335deg) brightness(80%) contrast(109%);
}

.post-likes-icon.animating {
  animation: pulse 0.3s ease-in-out;
}

@keyframes pulse {
  0% {
    transform: scale(1);
  }

  50% {
    transform: scale(1.1);
  }

  100% {
    transform: scale(1);
  }
}

.svg-filter-liked {
  filter: brightness(0) saturate(100%) invert(15%) sepia(87%) saturate(4756%) hue-rotate(335deg) brightness(80%) contrast(109%);
}

.view-more-wrapper {
  position: relative;
  display: flex;
  justify-content: center;
  align-items: center;
  height: auto;
  margin-top: 30px;
}

.view-more-wrapper button {
  color: #534f4f;
  background-color: transparent;
  font-size: 13px;
  font-family: sans-serif;
  font-weight: 600;
  border: none;
  border-bottom: 1px solid;
  border-color: #534f4f;
  padding-top: 10px;
  padding-left: 10px;
  padding-bottom: 4px;
  padding-right: 30px;
  cursor: pointer;
}

.view-more-wrapper button:hover {
  color: #69b1a9;
  border-color: #69b1a9;
}

@media (hover: none) {
  .view-more-wrapper button:hover {
    color: inherit;
    /* 元の色に戻す */
    border-color: inherit;
    /* 元の色に戻す */
  }
}

/* レスポンシブデザイン */
/* タブレット、スマートフォン向けのスタイル */
@media screen and (max-width: 1024px) {
  .post-image {
    width: 150px;
    height: 150px;
  }

  .post-title {
    font-size: 14px;
  }

  .post-text span {
    font-size: 13px;
  }

  .post-sub-wrapper {
    font-size: 12px;
  }

  .post-likes-icon {
    width: 25px;
    height: 25px;
  }

  .post-likes-wrapper span {
    font-size: 11px;
  }
}

@media screen and (max-width: 570px) {
  .post-whole-wrapper {
    width: 100%;
    margin-bottom: 0px;
  }

  .post-one-wrapper:nth-of-type(2n) {
    transform: translateX(0%);
  }

  .post-one-wrapper {
    padding: 0px;
    width: 100%;
    margin-bottom: 55px;
  }

  .post-main-wrapper {
    flex-direction: column;
    justify-content: center;
    align-items: center;
    height: auto;
  }

  .post-image {
    margin-bottom: 40px;
    width: 160px;
    height: 160px;
  }

  .post-texts-wrapper {
    padding-left: 0px;
  }

  .post-title {
    font-size: 13px;
  }

  .post-text span {
    display: -webkit-box;
    font-size: 12px;
    margin-bottom: 0px;
  }

  .post-sub-wrapper {
    transform: translateY(-20px);
    font-size: 10px;
  }

  .post-likes-icon {
    width: 35px;
    height: 35px;
  }

  .post-likes-wrapper span {
    font-size: 9px;
  }
}
</style>